import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, ArrowRight, Tag } from 'lucide-react';
import { useCart } from '../context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HomeSpares = ({ cms }) => {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const d = cms || {};
  const title = d.title || 'RO Spare Parts & Accessories';
  const subtitle = d.subtitle || 'Genuine Parts';

  useEffect(() => {
    const fetchParts = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/spare-parts/homepage/featured`);
        if (res.ok) setParts(await res.json());
      } catch (e) { console.error('Failed to fetch spare parts:', e); }
      finally { setLoading(false); }
    };
    fetchParts();
  }, []);

  if (loading) return null;
  if (parts.length === 0) return null;

  return (
    <section className="py-16 md:py-24 bg-gray-50 reveal-on-scroll" data-testid="home-spares-section">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
          <p className="text-xs sm:text-sm font-semibold text-lime-600 uppercase tracking-wider mb-3">{subtitle}</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
            {title}
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5 max-w-5xl mx-auto">
          {parts.map((part) => {
            const discount = part.originalPrice > part.price
              ? Math.round(((part.originalPrice - part.price) / part.originalPrice) * 100)
              : 0;
            return (
              <div key={part.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all group"
                data-testid={`home-spare-${part.id}`}>
                <div className="relative aspect-square bg-gray-50 overflow-hidden">
                  {part.image ? (
                    <img src={part.image} alt={part.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                      <Tag size={36} />
                    </div>
                  )}
                  {discount > 0 && (
                    <span className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                      {discount}% OFF
                    </span>
                  )}
                </div>
                <div className="p-3">
                  <p className="text-[10px] text-lime-600 font-semibold uppercase mb-0.5">{part.category}</p>
                  <h3 className="text-xs sm:text-sm font-semibold text-gray-900 leading-tight mb-1.5 line-clamp-2">{part.name}</h3>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm sm:text-base font-bold text-gray-900">{'\u20B9'}{part.price}</span>
                    {part.originalPrice > part.price && (
                      <span className="text-xs text-gray-400 line-through">{'\u20B9'}{part.originalPrice}</span>
                    )}
                  </div>
                  <button onClick={() => addToCart({ id: part.id, name: part.name, price: part.price, image: part.image, brand: part.category })}
                    className="w-full py-1.5 bg-lime-600 text-white text-xs font-semibold rounded-lg hover:bg-lime-700 transition-colors flex items-center justify-center gap-1"
                    data-testid={`add-spare-cart-${part.id}`}>
                    <ShoppingCart size={12} /> Add to Cart
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-8">
          <button onClick={() => navigate('/spare-parts')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-lime-600 text-white rounded-xl font-semibold hover:bg-lime-700 transition-colors text-sm"
            data-testid="view-all-spares-btn">
            View All Spare Parts <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default HomeSpares;
